import { ChangeDetectorRef, Component, Input, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { ColDef } from 'ag-grid-community';
import { GridConfig } from 'src/app/shared/grid-config';
import { AppConstants, MachineAttributesManagementConstants } from 'src/app/utilities/constant';
import { TranslateService } from '@ngx-translate/core';
import { ShareValuesService } from 'src/app/services/sharedValues/share-values.service';
import { BackendCallService } from 'src/app/services/backend-call/backend-call.service';
import { ConfirmationService } from 'src/app/services/confirmation.service';
import { ToastService } from '../../services/toast.service';
import { EditAddOnsComponent } from 'src/app/components/edit-add-ons/edit-add-ons.component';
import { ShowGridDataComponent } from '../show-grid-data/show-grid-data.component';

@Component({
  selector: 'app-add-edit-machine-attributes',
  templateUrl: './add-edit-machine-attributes.component.html',
  styleUrls: ['./add-edit-machine-attributes.component.scss'],
})
export class AddEditMachineAttributesComponent implements OnInit {
  @Input() attributesActiveTab;
  @ViewChild(ShowGridDataComponent) showGridDataComponent?: ShowGridDataComponent;
  
  gridColumnDefs: ColDef[] = [];
  gridConfigItems: GridConfig;
  machineAttributesInfo: any = [];
  count = 10;
  public loadMoreBinder: any;
  public searchText = '';
  public hasLoadedAttributes = false;
  private readonly backendOrderField = '__backendOrder';

  private columnAndConfigMap = {
    [MachineAttributesManagementConstants.APPLICATION]: {
      field: 'application',
      header: this.translate.instant('Application'),
      createLabel: this.translate.instant('Add'),
      trackerTitle: 'Application Tracker',
      fileName: 'Application.xlsx'
    },
    [MachineAttributesManagementConstants.MACHINE_TYPE]: {
      field: 'machine_type',
      header: this.translate.instant('Machine Type'),
      createLabel: this.translate.instant('Add'),
      trackerTitle: 'Machine Type Tracker',
      fileName: 'Machinetype.xlsx'
    },
    [MachineAttributesManagementConstants.SPECIFIC_FEATURES]: {
      field: 'specific_feature',
      header: this.translate.instant('Specific Features'),
      createLabel: this.translate.instant('Add'),
      trackerTitle: 'Specific Features Tracker',
      fileName: 'SpecificFeatures.xlsx'
    },
    [MachineAttributesManagementConstants.CONTROL_SYSTEM]: {
      field: 'control_system',
      header: this.translate.instant('Control System'),
      createLabel: this.translate.instant('Add'),
      trackerTitle: 'Control System Tracker',
      fileName: 'ControlSystem.xlsx'
    }
  };
  private attributeFetchConfig = {
    'Application': {
      field: 'application',
      service: (payload) => this.backendCallService.getApplicationData(payload)
    },
    'Machine Type': {
      field: 'machine_type',
      service: (payload) => this.backendCallService.getMachineTypeData(payload)
    },
    'Specific Features': {
      field: 'specific_feature',
      service: (payload) => this.backendCallService.getFeaturesData(payload)
    },
    'Control System': {
      field: 'control_system',
      service: (payload) => this.backendCallService.getControlSystemData(payload)
    }
  };
  // This field map is used to get the field name from the attribute name and pass to edit-add-ons component
  private attributeFieldMap = {
    'Application': 'application',
    'Machine Type': 'machine_type',
    'Specific Features': 'specific_feature',
    'Control System': 'control_system'
  };
  // This config map is used to call the respective service for edit selected attribute and show error message in case of conflict
  private attributeEditConfig = {
    'Application': {
      field: 'application',
      service: (data) => this.backendCallService.machineApplicationData(data),
      errorMsg: this.translate.instant('application_type_exists_msg')
    },
    'Machine Type': {
      field: 'machineType',
      service: (data) => this.backendCallService.machineMachineTypeData(data),
      errorMsg: this.translate.instant('machine_type_exists_msg')
    },
    'Specific Features': {
      field: 'specificFeature',
      service: (data) => this.backendCallService.machineSpecificFeatureData(data),
      errorMsg: this.translate.instant('specific_feature_exists_msg')
    },
    'Control System': {
      field: 'controlSystem',
      service: (data) => this.backendCallService.machineControlSystemData(data),
      errorMsg: this.translate.instant('control_system_exists_msg')
    }
  };

  constructor(
    public translate: TranslateService,
    public sharedService: ShareValuesService,
    private changeDetectorRef: ChangeDetectorRef,
    public modalController: ModalController,
    private toastService: ToastService,
    private confirmationService: ConfirmationService,
    private backendCallService: BackendCallService
  ) { }

  ngOnInit() {
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.attributesActiveTab) {
      const selectedValue = changes.attributesActiveTab.currentValue;
      this.attributesActiveTab = selectedValue;
      this.searchText = '';
      this.fnCreateColumnDefsAndConfigItems();
      this.getMachineAttributesData();
    }
  }

  private getAttributeEntityLabel(): string {
    return this.translate.instant(this.attributesActiveTab === 'Specific Features' ? 'Specific Feature' : this.attributesActiveTab);
  }

  private getAttributeOperationMessage(action: 'Create' | 'Edit' | 'Delete'): string {
    const key = action === 'Create'
      ? 'customer_management_created_successfully'
      : action === 'Edit'
        ? 'customer_management_updated_successfully'
        : 'customer_management_deleted_successfully';

    return this.translate.instant(key, { entity: this.getAttributeEntityLabel() });
  }

  getMachineAttributesData(param?: number) {
    this.machineAttributesInfo = [];
    this.hasLoadedAttributes = false;
    this.loadMoreBinder = (offset?: number) => this.fetchAttributes(this.attributesActiveTab, offset);
    this.changeDetectorRef.detectChanges();
    this.loadMoreBinder(param);
  }

  private fetchAttributes(tab: string, param?: number) {
    const config = this.attributeFetchConfig[tab];
    if (!config) {
      console.warn('Unknown tab:', tab);
      return;
    }
    const offset = param || 0;
    const searchValue =  (this.searchText || '').trim() || ' ' ;
    const payload = {
      search: searchValue,
      count: this.count,
      increasedCount: offset,
    };
    config.service(payload).subscribe({
      next: (data: any) => {
        const items = Array.isArray(data?.value) ? data.value : [];
        const backendOrderStart = this.machineAttributesInfo.length;
        const newData = items
        .filter((ele: any) => ele[config.field] !== 'NA')
        .map((element: any, index: number) => this.createAttributeGridRow(element, config.field, backendOrderStart + index));
        const isLoadMore = offset > 0;
        const merged = [...this.machineAttributesInfo, ...newData];
        this.machineAttributesInfo = Array.from(
          new Map(merged.map(item => [item.id, item])).values()
        );
        this.hasLoadedAttributes = true;
        if (isLoadMore) {
          this.showGridDataComponent?.appendGridData(newData);
        } else {
          this.showGridDataComponent?.replaceGridData(this.machineAttributesInfo);
        }
        console.log("this.machineAttributesInfo", this.machineAttributesInfo)
        this.changeDetectorRef.detectChanges();
      },
      error: (err) => {
        this.hasLoadedAttributes = true;
        console.error(err);
        this.sharedService.isGridDataLoading = false;
      }
    });
  }

  private createAttributeGridRow(element: any, field: string, backendOrder: number) {
    const row = {
      id: element.id,
      [field]: element[field],
      isEdited: false,
      isDelete: false,
      associatedMachineCount: element?.associatedMachineCount || 0
    };

    Object.defineProperty(row, this.backendOrderField, {
      value: backendOrder,
      enumerable: false,
      configurable: true
    });

    return row;
  }

  fnCreateColumnDefsAndConfigItems() {
    const config = this.columnAndConfigMap[this.attributesActiveTab];
    if (!config) {
      console.warn('Unknown tab:', this.attributesActiveTab);
      return;
    }
    this.gridColumnDefs = [
      {
        headerName: config.header,
        field: config.field,
        minWidth: 150,
        sortable: true,
        suppressMenu: true,
        cellClass: 'grid-column-value grid-hover-link'
      },
      {
        headerName: this.translate.instant('associated_machine_count'),
        field: 'associatedMachineCount',
        minWidth: 150,
        sortable: true,
        suppressMenu: true,
        cellClass: 'grid-column-value grid-hover-link'
      },
      {
        headerName: this.translate.instant('Actions'),
        cellRenderer: 'actionRenderer',
        field: 'userActionField',
        suppressMovable: true,
        suppressMenu: true,
        sortable: false,
        minWidth: 150,
        maxWidth: 250,
        cellClass: 'float-right',
        headerComponentParams: [
          {
            buttonClass: 'more-icon',
            buttonTooltip: this.translate.instant('Edit'),
            userAction: this.translate.instant('Edit'),
            url: 'assets/images/edit.svg',
          },
          {
            buttonClass: 'more-icon',
            buttonTooltip: this.translate.instant('Delete'),
            userAction: this.translate.instant('Delete'),
            url: 'assets/images/Delete.svg',
          }
        ]
      }
    ];

    this.gridConfigItems = new GridConfig(
      this.backendOrderField,
      config.field,
      false,
      true,
      true,
      false,
      'asc',
      40,
      'no_data_available',
      '',
      config.header,
      config.createLabel,
      true,
      1,
      'grid-wrapper-height-40',
      config.trackerTitle,
      config.fileName,
      false,
      false
    );
  }

  onSearch(searchText: string = '') {
    this.searchText = searchText ? searchText : '';
    this.machineAttributesInfo = [];
    this.sharedService.isGridDataLoading = true;
    this.getMachineAttributesData();
  }

  onGridCellClick(params) {
    if (params.data.userAction == this.translate.instant('Edit')) {
      this.fnEditItem(params.data);
    }
    else if (params.data.userAction == this.translate.instant('Delete')) {
      this.fnDeleteItem(params.data);
    }
  }

  async fnEditItem(attribute) {
    this.openAttributeModal(false, attribute);
  }

  async onAddBtnClick(event) {
    this.openAttributeModal(true);
  }

  async openAttributeModal(isCreate: boolean, attribute?: any) {
    const config = this.attributeEditConfig[this.attributesActiveTab];
    if (!config) {
      console.warn('Unknown tab:', this.attributesActiveTab);
      return;
    }
    const addOns = isCreate ? [{ id: '', type: '', machine_Id: '', isEdited: true, isDelete: false, associatedMachineCount: 0}]
      : [{...attribute, machineTitle: this.attributeFieldMap[this.attributesActiveTab] ? attribute[this.attributeFieldMap[this.attributesActiveTab]] : ''}];
    const props: any = { title: this.attributesActiveTab, data: addOns };
    if (isCreate) {
      props.action = this.translate.instant('Create');
    }
    const modal = await this.modalController.create({
      component: EditAddOnsComponent,
      cssClass: 'edit-add-on-class',
      backdropDismiss: false,
      showBackdrop: false,
      componentProps: props,
    });
    await modal.present();
    const { data: { isActionConfirmed, updatedData } } = await modal.onWillDismiss();
    if (isActionConfirmed) {
      this.addOrEditAttribute(updatedData, config, isCreate ? 'Create' : 'Edit');
    }
  }

  private addOrEditAttribute(updatedData: any[], config: any, action: 'Create' | 'Edit') {
    updatedData.forEach(item => {
      item[config.field] = item?.machineTitle;
      item.isEdited = true;
    });
    config.service(updatedData).subscribe({
      next: (attributeDataResult: any) => {
        this.toastService.presentToast(this.getAttributeOperationMessage(action), 4000, AppConstants.SUCCESS_SNACKBAR, 'top');
        this.getMachineAttributesData();
      },
      error: (err) => {
        if (err.status === 409) {
          this.toastService.presentToast(config.errorMsg, 4000, AppConstants.ERROR_SNACKBAR, 'top');
        }
      }
    });
  }

  async fnDeleteItem(attribute) {
    if (attribute.associatedMachineCount > 1) {
      // Show toast and prevent deletion
      this.toastService.presentToast(
        this.translate.instant('delete_attribute_error'),
        4000,
        AppConstants.ERROR_SNACKBAR,
        'top'
      );
      return;
    }
    const item = this.translate.instant(this.attributesActiveTab).toLowerCase();
    const itemName = attribute.application || attribute.machine_type || attribute.specific_feature || attribute.control_system;
    const title = this.translate.instant('Are_you_sure_you_want_to_delete') + ' ' + item + '?';
    const subtitle = `'${itemName}'`;
    const actionText = this.translate.instant('Delete');
    const isConfirmed = await this.confirmationService.presentConfirmationModal(
      title,
      actionText,
      true,
      this.translate.instant('Cancel'),
      subtitle
    );
    if (isConfirmed) {
      this.performAttributeDelete(attribute);
    }
  }

  performAttributeDelete(attribute) {
    const payload: any = {
      id: attribute.id,
      machine_Id: '',
    };
    let deleteObservable;
    switch (this.attributesActiveTab) {
      case 'Application':
        payload.application = attribute.type;
        deleteObservable = this.backendCallService.deleteApplicationData(payload);
        break;
      case 'Machine Type':
        payload.machineType = attribute.type;
        deleteObservable = this.backendCallService.deleteMachineType(payload);
        break;
      case 'Specific Features':
        payload.specificFeature = attribute.type;
        deleteObservable = this.backendCallService.deleteSpecificFeature(payload);
        break;
      case 'Control System':
        payload.controlSystem = attribute.type;
        deleteObservable = this.backendCallService.deleteControlSystem(payload);
        break;
      default:
        return;
    }
    deleteObservable.subscribe((res: any) => {
      this.toastService.presentToast(this.getAttributeOperationMessage('Delete'), 4000, AppConstants.SUCCESS_SNACKBAR, 'top');
      this.getMachineAttributesData();
    });
  }

}